/*
  See Cosmora in Action (sections/cosmora-action-videos.liquid).

  No <video> exists until a tap. The element is built and play() is called
  synchronously inside the click handler: iOS WebKit (Instagram and Facebook
  webviews included) only honours play() within the user gesture. It plays
  inline and muted; the speaker button unmutes. Starting one clip pauses the
  others, and a clip that scrolls or swipes out of view pauses.
*/
if (!customElements.get('cosmora-action-videos')) {
  customElements.define(
    'cosmora-action-videos',
    class CosmoraActionVideos extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('.cosmora-action__track');
        this.prev = this.querySelector('.cosmora-action__nav--prev');
        this.next = this.querySelector('.cosmora-action__nav--next');
        if (!this.track) return;

        this.addEventListener('click', this.onClick.bind(this));
        this.onScroll = this.onScroll.bind(this);
        this.track.addEventListener('scroll', this.onScroll, { passive: true });
        this.updateNav();
        this.observePosters();

        if ('IntersectionObserver' in window) {
          this.visibility = new IntersectionObserver(
            (entries) => {
              entries.forEach((entry) => {
                const video = entry.target.querySelector('video');
                if (video && !video.paused && entry.intersectionRatio < 0.25) video.pause();
              });
            },
            { threshold: [0, 0.25] }
          );
        }
      }

      // Posters render without src; fill them in once the section is within 200px of the viewport.
      observePosters() {
        const load = () =>
          this.querySelectorAll('img[data-src]').forEach((img) => {
            if (img.dataset.srcset) img.srcset = img.dataset.srcset;
            img.src = img.dataset.src;
            img.removeAttribute('data-src');
            img.removeAttribute('data-srcset');
          });
        if (!('IntersectionObserver' in window)) return load();
        this.posters = new IntersectionObserver(
          (entries) => {
            if (!entries.some((entry) => entry.isIntersecting)) return;
            this.posters.disconnect();
            load();
          },
          { rootMargin: '0px 0px 200px 0px' }
        );
        this.posters.observe(this);
      }

      disconnectedCallback() {
        if (this.posters) this.posters.disconnect();
        if (this.visibility) this.visibility.disconnect();
        if (this.track) this.track.removeEventListener('scroll', this.onScroll);
      }

      onClick(event) {
        const sound = event.target.closest('.cosmora-action__sound');
        if (sound) return this.toggleSound(sound);

        const play = event.target.closest('.cosmora-action__play');
        if (play) return this.toggle(play.closest('.cosmora-action__card'), play);

        const nav = event.target.closest('.cosmora-action__nav');
        if (nav) this.scrollByCard(nav === this.prev ? -1 : 1);
      }

      toggle(card, button) {
        const video = card.querySelector('video') || this.createVideo(card, button);
        if (!video.paused && !video.ended) {
          video.pause();
          return;
        }

        this.querySelectorAll('.cosmora-action__card video').forEach((other) => {
          if (other !== video && !other.paused) other.pause();
        });

        card.classList.add('is-loading');
        const attempt = video.play();
        if (attempt && typeof attempt.catch === 'function') {
          attempt.catch(() => card.classList.remove('is-loading'));
        }

        if (document.activeElement === button) {
          card.querySelector('.cosmora-action__sound').focus({ preventScroll: true });
          button.focus({ preventScroll: true });
        }
      }

      createVideo(card, button) {
        const video = document.createElement('video');
        video.className = 'cosmora-action__video';
        video.muted = true;
        video.defaultMuted = true;
        video.playsInline = true;
        video.setAttribute('muted', '');
        video.setAttribute('playsinline', '');
        video.setAttribute('webkit-playsinline', '');
        video.setAttribute('disablepictureinpicture', '');
        video.setAttribute('disableremoteplayback', '');
        video.setAttribute('preload', 'auto');
        if (card.dataset.poster) video.poster = card.dataset.poster;

        const source = document.createElement('source');
        source.src = card.dataset.src;
        source.type = card.dataset.type || 'video/mp4';
        video.appendChild(source);

        video.addEventListener('playing', () => {
          card.classList.remove('is-loading');
          card.classList.add('is-playing', 'has-played');
          button.setAttribute('aria-pressed', 'true');
        });
        video.addEventListener('waiting', () => card.classList.add('is-loading'));
        const stop = () => {
          card.classList.remove('is-playing', 'is-loading');
          button.setAttribute('aria-pressed', 'false');
        };
        video.addEventListener('pause', stop);
        video.addEventListener('ended', stop);
        video.addEventListener('error', stop);

        card.insertBefore(video, card.firstChild);
        card.appendChild(this.createSoundButton());
        if (this.visibility) this.visibility.observe(card);
        return video;
      }

      createSoundButton() {
        const sound = document.createElement('button');
        sound.type = 'button';
        sound.className = 'cosmora-action__sound';
        sound.setAttribute('aria-label', this.dataset.soundOnLabel || '');
        sound.innerHTML =
          '<svg class="cosmora-action__sound-off" aria-hidden="true" focusable="false" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 5 6 9H3v6h3l5 4z" fill="currentColor"/><path d="m17 9 5 6M22 9l-5 6"/></svg>' +
          '<svg class="cosmora-action__sound-on" aria-hidden="true" focusable="false" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 5 6 9H3v6h3l5 4z" fill="currentColor"/><path d="M15.5 8.5a5 5 0 0 1 0 7M18.5 5.5a9 9 0 0 1 0 13"/></svg>';
        return sound;
      }

      toggleSound(sound) {
        const video = sound.closest('.cosmora-action__card').querySelector('video');
        if (!video) return;
        video.muted = !video.muted;
        if (!video.muted && video.paused) video.play().catch(() => {});
        sound.classList.toggle('is-unmuted', !video.muted);
        sound.setAttribute('aria-label', video.muted ? this.dataset.soundOnLabel : this.dataset.soundOffLabel);
      }

      scrollByCard(direction) {
        const item = this.track.querySelector('.cosmora-action__item');
        if (!item) return;
        const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
        this.track.scrollBy({ left: direction * (item.offsetWidth + gap), behavior: 'smooth' });
      }

      onScroll() {
        if (this.navFrame) return;
        this.navFrame = requestAnimationFrame(() => {
          this.navFrame = null;
          this.updateNav();
        });
      }

      updateNav() {
        if (!this.prev || !this.next) return;
        const max = this.track.scrollWidth - this.track.clientWidth;
        this.prev.disabled = this.track.scrollLeft <= 2;
        this.next.disabled = this.track.scrollLeft >= max - 2;
      }
    }
  );
}
